var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineComponent as _defineComponent } from 'vue';
import { library } from "@fortawesome/fontawesome-svg-core";
import { faStar, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { BAlert, BButton, BNav, BNavItem, BOverlay, BPagination } from "bootstrap-vue";
import { filter } from "underscore";
import { computed, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router/composables";
import { loadWorkflows } from "@/components/Workflow/workflows.services";
import { Toast } from "@/composables/toast";
import { useUserStore } from "@/stores/userStore";
import { helpHtml, WorkflowFilters } from "./WorkflowFilters";
import FilterMenu from "@/components/Common/FilterMenu.vue";
import Heading from "@/components/Common/Heading.vue";
import ListHeader from "@/components/Common/ListHeader.vue";
import LoginRequired from "@/components/Common/LoginRequired.vue";
import LoadingSpan from "@/components/LoadingSpan.vue";
import WorkflowCard from "@/components/Workflow/WorkflowCard.vue";
import WorkflowListActions from "@/components/Workflow/WorkflowListActions.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'WorkflowList',
    props: {
        activeList: { default: "my" }
    },
    setup(__props) {
        const props = __props;
        library.add(faStar, faTrash);
        const router = useRouter();
        const userStore = useUserStore();
        const limit = ref(24);
        const offset = ref(0);
        const loading = ref(true);
        const overlay = ref(false);
        const filterText = ref("");
        const totalWorkflows = ref(0);
        const showAdvanced = ref(false);
        const showBookmarked = ref(false);
        const listHeader = ref(null);
        const workflowsLoaded = ref([]);
        const searchPlaceHolder = computed(() => {
            let placeHolder = "Search my workflows";
            if (published.value) {
                placeHolder = "Search published workflows";
            }
            else if (sharedWithMe.value) {
                placeHolder = "Search workflows shared with me";
            }
            placeHolder += " by query or use the advanced filtering options";
            return placeHolder;
        });
        const published = computed(() => props.activeList === "published");
        const sharedWithMe = computed(() => props.activeList === "shared_with_me");
        const showDeleted = computed(() => filterText.value.includes("is:deleted"));
        const currentPage = computed(() => Math.floor(offset.value / limit.value) + 1);
        const view = computed(() => userStore.preferredListViewMode || "grid");
        const sortDesc = computed(() => { var _a; return (_a = (listHeader.value && listHeader.value.sortDesc)) !== null && _a !== void 0 ? _a : true; });
        const sortBy = computed(() => (listHeader.value && listHeader.value.sortBy) || "update_time");
        const noItems = computed(() => !loading.value && workflowsLoaded.value.length === 0 && !filterText.value);
        const noResults = computed(() => !loading.value && workflowsLoaded.value.length === 0 && filterText.value);
        const deleteButtonTitle = computed(() => (showDeleted.value ? "Hide deleted workflows" : "Show deleted workflows"));
        const bookmarkButtonTitle = computed(() => showBookmarked.value ? "Hide bookmarked workflows" : "Show bookmarked workflows");
        // Filtering computed refs
        const workflowFilters = computed(() => WorkflowFilters(props.activeList));
        const rawFilters = computed(() => Object.fromEntries(workflowFilters.value.getFiltersForText(filterText.value, true, false)));
        const validFilters = computed(() => workflowFilters.value.getValidFilters(rawFilters.value, true).validFilters);
        const invalidFilters = computed(() => workflowFilters.value.getValidFilters(rawFilters.value, true).invalidFilters);
        const isSurroundedByQuotes = computed(() => /^["'].*["']$/.test(filterText.value));
        const hasInvalidFilters = computed(() => !isSurroundedByQuotes.value && Object.keys(invalidFilters.value).length > 0);
        function updateFilterValue(filterKey, newValue) {
            const currentFilterText = filterText.value;
            filterText.value = workflowFilters.value.setFilterValue(currentFilterText, filterKey, newValue);
        }
        function toggleBookmarked(bookmarked) {
            showBookmarked.value = bookmarked !== null && bookmarked !== void 0 ? bookmarked : !showBookmarked.value;
        }
        function onToggleBookmarked() {
            toggleBookmarked();
        }
        function onToggleDeleted() {
            updateFilterValue("deleted", true);
            toggleBookmarked(false);
        }
        function load(overlayLoading = false, silent = false) {
            return __awaiter(this, void 0, void 0, function* () {
                if (!silent) {
                    if (overlayLoading) {
                        overlay.value = true;
                    }
                    else {
                        loading.value = true;
                    }
                }
                let search;
                if (!hasInvalidFilters.value) {
                    search = validatedFilterText();
                    // append default backend query filters for provided `props.activeList`
                    if (published.value && !workflowFilters.value.getFilterValue(search, "published")) {
                        search += " is:published";
                    }
                    if (sharedWithMe.value && !workflowFilters.value.getFilterValue(search, "shared_with_me")) {
                        search += " is:shared_with_me";
                    }
                }
                else {
                    // there are invalid filters, so we don't want to search
                    overlay.value = false;
                    loading.value = false;
                    return;
                }
                try {
                    const { data, headers } = yield loadWorkflows({
                        sortBy: sortBy.value,
                        sortDesc: sortDesc.value,
                        limit: limit.value,
                        offset: offset.value,
                        filterText: search === null || search === void 0 ? void 0 : search.trim(),
                        showPublished: published.value,
                        skipStepCounts: true,
                    });
                    let filteredWorkflows = showBookmarked.value
                        ? filter(data, (workflow) => workflow.show_in_tool_panel)
                        : data;
                    if (props.activeList === "my") {
                        filteredWorkflows = filter(filteredWorkflows, (w) => { var _a; return w.owner === ((_a = userStore.currentUser) === null || _a === void 0 ? void 0 : _a.username); });
                    }
                    workflowsLoaded.value = filteredWorkflows;
                    if (showBookmarked.value) {
                        totalWorkflows.value = filteredWorkflows.length;
                    }
                    else {
                        totalWorkflows.value = parseInt(headers.get("Total_matches") || "0", 10) || 0;
                    }
                }
                catch (e) {
                    Toast.error(`Failed to load workflows: ${e}`);
                }
                finally {
                    overlay.value = false;
                    loading.value = false;
                }
            });
        }
        function onPageChange(page) {
            return __awaiter(this, void 0, void 0, function* () {
                offset.value = (page - 1) * limit.value;
                yield load(true);
            });
        }
        function validatedFilterText() {
            if (isSurroundedByQuotes.value) {
                // the `filterText` is surrounded by quotes, remove them
                return filterText.value.slice(1, -1);
            }
            else if (Object.keys(rawFilters.value).length === 0) {
                // there are no filters derived from the `filterText`
                return filterText.value;
            }
            // there are valid filters derived from the `filterText`
            return workflowFilters.value.getFilterText(validFilters.value, true);
        }
        watch([filterText, sortBy, sortDesc, showBookmarked], () => __awaiter(this, void 0, void 0, function* () {
            offset.value = 0;
            yield load(true);
        }));
        onMounted(() => {
            if (router.currentRoute.query.owner) {
                updateFilterValue("user", `'${router.currentRoute.query.owner}'`);
            }
            load();
        });
        return { __sfc: true, props, router, userStore, limit, offset, loading, overlay, filterText, totalWorkflows, showAdvanced, showBookmarked, listHeader, workflowsLoaded, searchPlaceHolder, published, sharedWithMe, showDeleted, currentPage, view, sortDesc, sortBy, noItems, noResults, deleteButtonTitle, bookmarkButtonTitle, workflowFilters, rawFilters, validFilters, invalidFilters, isSurroundedByQuotes, hasInvalidFilters, updateFilterValue, toggleBookmarked, onToggleBookmarked, onToggleDeleted, load, onPageChange, validatedFilterText, faStar, faTrash, FontAwesomeIcon, BAlert, BButton, BNav, BNavItem, BOverlay, BPagination, helpHtml, FilterMenu, Heading, ListHeader, LoginRequired, LoadingSpan, WorkflowCard, WorkflowListActions };
    }
});
